import React, { useState } from 'react';
import Reveal from '../components/Reveal';
import { ArrowRight, Check, Copy, Mail, Pin } from '../components/Icons';
import usePageTitle from '../hooks/usePageTitle';
import { CONTACT } from '../data/content';
import './ContactPage.css';

const PREFERENCES = ['Email', 'Video call', 'Phone call', 'Chat'];
const MAX_MESSAGE = 1200;
const EMPTY = { name: '', email: '', subject: '', message: '', preference: 'Email' };

const validate = (data) => {
  const errors = {};
  if (!data.name.trim()) errors.name = 'Please tell us your name.';
  if (!data.email.trim()) errors.email = 'We need an email address to reply to.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) errors.email = 'That email address looks incomplete.';
  if (!data.message.trim()) errors.message = 'Add a few words about your project.';
  return errors;
};

const ContactPage = () => {
  usePageTitle('Contact');
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  // No backend yet: hand the message to the visitor's email app, ready to send.
  const handleSubmit = (e) => {
    e.preventDefault();
    const found = validate(form);
    setErrors(found);
    if (Object.keys(found).length) {
      document.getElementById(Object.keys(found)[0])?.focus();
      return;
    }
    const subject = form.subject.trim() || `New project enquiry from ${form.name}`;
    const body = `${form.message}\n\n—\nName: ${form.name}\nEmail: ${form.email}\nPreferred contact: ${form.preference}`;
    window.location.href = `mailto:${CONTACT.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  const copyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT.email);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Clipboard can be blocked; the address is still visible to copy by hand.
    }
  };

  const fieldProps = (name) => ({
    id: name,
    name,
    value: form[name],
    onChange: handleChange,
    'aria-invalid': Boolean(errors[name]),
    'aria-describedby': errors[name] ? `${name}-error` : undefined,
  });

  const FieldError = ({ name }) =>
    errors[name] ? (
      <p id={`${name}-error`} className="field-error">
        {errors[name]}
      </p>
    ) : null;

  return (
    <div className="contact-page">
      <section className="page-hero">
        <div className="container">
          <p className="eyebrow">Contact</p>
          <h1>
            Let's talk, <span className="highlight">your way.</span>
          </h1>
          <p className="lead">
            Get in touch to discuss your project or ask any questions. Tell us how you like to communicate and we'll
            meet you there.
          </p>
        </div>
      </section>

      <section className="section">
        <div className="container contact-grid">
          <Reveal className="contact-info">
            <h2>Get in touch</h2>
            <p className="lead">
              We're excited to hear about your project. Fill out the form and we'll get back to you as soon as
              possible.
            </p>

            <ul className="contact-methods">
              <li className="contact-method">
                <span className="contact-icon">
                  <Mail />
                </span>
                <div>
                  <h3>Email</h3>
                  <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
                </div>
                <button type="button" className="copy-btn" onClick={copyEmail} aria-label="Copy email address">
                  {copied ? <Check /> : <Copy />}
                  <span aria-live="polite">{copied ? 'Copied' : 'Copy'}</span>
                </button>
              </li>
              <li className="contact-method">
                <span className="contact-icon">
                  <Pin />
                </span>
                <div>
                  <h3>Location</h3>
                  <p>{CONTACT.location}</p>
                </div>
              </li>
            </ul>

            <div className="promise">
              <h3>Our communication promise</h3>
              <ul>
                <li>Replies in writing, so you can read them in your own time</li>
                <li>No surprise calls; every meeting has an agenda</li>
                <li>Plain language, zero jargon</li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={120} className="contact-form-wrap">
            {sent ? (
              <div className="form-success" role="status">
                <span className="success-icon">
                  <Check />
                </span>
                <h2>Your email is ready to send.</h2>
                <p>
                  We've opened your email app with your message filled in. Hit send there and we'll be in touch. If
                  nothing opened, email us directly at <a href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>.
                </p>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setForm(EMPTY);
                    setSent(false);
                  }}
                >
                  Write another message
                </button>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">
                      Name <span className="required">*</span>
                    </label>
                    <input type="text" autoComplete="name" {...fieldProps('name')} />
                    <FieldError name="name" />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">
                      Email <span className="required">*</span>
                    </label>
                    <input type="email" autoComplete="email" {...fieldProps('email')} />
                    <FieldError name="email" />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="subject">Subject</label>
                  <input type="text" {...fieldProps('subject')} />
                </div>

                <fieldset className="form-group">
                  <legend>How would you like us to reach you?</legend>
                  <div className="pref-chips">
                    {PREFERENCES.map((pref) => (
                      <label key={pref} className="pref-chip">
                        <input
                          type="radio"
                          name="preference"
                          value={pref}
                          checked={form.preference === pref}
                          onChange={handleChange}
                        />
                        <span>{pref}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>

                <div className="form-group">
                  <label htmlFor="message">
                    Message <span className="required">*</span>
                  </label>
                  <textarea rows="6" maxLength={MAX_MESSAGE} {...fieldProps('message')} />
                  <div className="field-foot">
                    <FieldError name="message" />
                    <span className="char-count">
                      {form.message.length}/{MAX_MESSAGE}
                    </span>
                  </div>
                </div>

                <button type="submit" className="btn submit-btn">
                  Send message <ArrowRight />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
